/**
 * React Context for language management.
 * Provides t() translation function and language switching.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { translate, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './index';

const STORAGE_KEY = 'fokusgrupp_language';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED_LANGUAGES.some(l => l.code === stored)) {
        return stored;
      }
    } catch (_) {
      // localStorage not available
    }
    return DEFAULT_LANGUAGE;
  });

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (_) {
      // localStorage not available
    }
  }, []);

  // Update document lang attribute and title
  useEffect(() => {
    document.documentElement.lang = language;
    document.title = translate(language, 'app.documentTitle');
  }, [language]);

  const t = useCallback((key, params) => {
    return translate(language, key, params);
  }, [language]);

  const value = { language, setLanguage, t };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access translation function and language state.
 * @returns {{ t: (key: string, params?: Object) => string, language: string, setLanguage: (lang: string) => void }}
 */
export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}

export { SUPPORTED_LANGUAGES };
export default LanguageContext;
